import { useContext } from "react";
import { useStores } from "@/hooks/useStores";
import { AudioContext, AudioState } from "@/context/audio";
import { trackEvent } from "@/core/analytics";
import { getPodcastImage, ImageSizes } from "@/core/helpers";

type ClickEvent = React.MouseEvent<HTMLElement> | undefined;

/**
 * Shared play/pause logic for an episode, used by every control that starts playback.
 * `startAt` (seconds) overrides where playback begins, e.g. for a shared link to a moment.
 */
export const usePlayEpisode = (episode: Episode, podcast: Podcast, startAt?: number | null) => {
    const { listeningStore, playerStore } = useStores();
    const sound = useContext<AudioState>(AudioContext);

    const play = (event?: ClickEvent) => {
        event?.preventDefault();
        const isSwitchingEpisode = playerStore.currentEpisode.sound !== episode.sound;

        if (isSwitchingEpisode) {
            if (playerStore.currentEpisode.sound !== "") {
                sound.pause();
            }
            const resumeAt = startAt ?? listeningStore.resumePosition(episode.id);
            sound.setAudio(
                {
                    id: episode.id.toString(),
                    src: episode.sound,
                    title: episode.title,
                    artist: podcast.name,
                    album: podcast.name,
                    artwork: getPodcastImage(podcast, ImageSizes.Medium),
                },
                resumeAt
            );
            playerStore.setCurrentEpisode(episode, podcast);
        }

        if (!isSwitchingEpisode && startAt !== null && startAt !== undefined) {
            sound.seek(startAt);
        }

        const currentProgress = isSwitchingEpisode
            ? (startAt ?? listeningStore.resumePosition(episode.id))
            : Math.floor(sound.seek());
        trackEvent({
            category: "Player",
            action: "Play",
            label: `${podcast.slug}-${episode.slug}`,
            value: currentProgress,
        });
        playerStore.play(currentProgress);
        sound.play();
    };

    const pause = (event?: ClickEvent) => {
        event?.preventDefault();

        const currentProgress = parseInt(sound.seek().toString());
        trackEvent({
            category: "Player",
            action: "Pause",
            label: `${podcast.slug}-${episode.slug}`,
            value: currentProgress,
        });
        playerStore.pause(currentProgress);
        sound.pause();
    };

    const isPlaying = playerStore.currentEpisode.title === episode.title && sound.isPlaying;

    return { play, pause, isPlaying };
};
